/**
 * Pure derivations for a rehab project's surface UI state.
 *
 * The DB schema captures detail (7 stages, start/target/actual dates, line
 * item totals). The rehab.html design distills this down to three statuses
 * and a single progress percent. Keeping the mapping in a pure module makes
 * it testable and reusable across the cards and KPI row.
 */
import type { RehabProject } from "@/hooks/use-rehab";

import { totalRecognizedSpend, type ScopeItem } from "./rehab-scope";

export type RehabStatus = "not_started" | "in_progress" | "completed";

/**
 * The `rehab_stage` enum now maps 1:1 onto the three UI statuses, so this is
 * effectively an identity — kept as a function so callers don't depend on the
 * column shape directly.
 */
export function deriveStatus(project: RehabProject): RehabStatus {
  return project.stage;
}

/**
 * Progress percent for the card's progress bar:
 *   - completed   → 100
 *   - not_started → 0
 *   - in_progress → share of completed line items (same basis the drawer uses,
 *                   so card and drawer agree). 0 when the project has no
 *                   budgeted line items yet (nothing scoped → nothing done).
 *
 * `line_total_count` / `line_completed_count` are aggregated in `use-rehab` and
 * already exclude zero-estimate rows, so this counts only budgeted items — a
 * scope full of $0 placeholders never drags the percentage down.
 */
export function deriveProgressPct(project: RehabProject): number {
  const status = deriveStatus(project);
  if (status === "completed") return 100;
  if (status === "not_started") return 0;
  const total = Number(project.line_total_count) || 0;
  const done = Number(project.line_completed_count) || 0;
  if (total <= 0) return 0;
  return Math.round((done / total) * 100);
}

/**
 * Display-ready budget figures. Prefer summed line items (truthier), fall
 * back to the project-level budget/spent when no lines exist yet.
 *
 * `budget` spans every line item; `used` counts only the ones marked
 * completed (see `recognizedSpend`), so the bar fills as work is finished
 * rather than as amounts are typed against pending tasks.
 */
export interface BudgetView {
  budget: number;
  used: number;
  pct: number;
  overBudget: boolean;
}

export function deriveBudget(project: RehabProject): BudgetView {
  const lineEst = Number(project.line_estimated_total) || 0;
  const lineAct = Number(project.line_actual_total) || 0;
  const budget = lineEst > 0 ? lineEst : Number(project.total_budget) || 0;
  const used = lineEst > 0 ? lineAct : Number(project.total_spent) || 0;
  const pct = budget > 0 ? Math.round((used / budget) * 100) : 0;
  return { budget, used, pct, overBudget: used > budget && budget > 0 };
}

/**
 * Budget figures derived from the live scope items (the drawer's
 * `rehab_line_items` query). Use this while the drawer is open so the Key
 * Stats stay in lockstep with the chart and checklist — including custom
 * items and freshly toggled tasks — without waiting for the project-level
 * totals (`line_*_total`) to be refetched.
 */
export function deriveBudgetFromItems(items: ScopeItem[]): BudgetView {
  const budget = items.reduce((s, i) => s + (Number(i.budget) || 0), 0);
  const used = totalRecognizedSpend(items);
  const pct = budget > 0 ? Math.round((used / budget) * 100) : 0;
  return { budget, used, pct, overBudget: used > budget && budget > 0 };
}

/** Per-project rollup of rehab_line_items. */
export interface LineTotals {
  /** Sum of estimated_cost across ALL rows — the budget cap. */
  est: number;
  /**
   * Sum of actual_cost across COMPLETED rows — the money recognized as spent.
   * Pending rows contribute nothing regardless of what's recorded on them.
   */
  act: number;
  /** Count of BUDGETED (estimated_cost > 0) rows marked completed. */
  done: number;
  /** Count of BUDGETED (estimated_cost > 0) rows. */
  total: number;
}

/** Minimal rehab_line_items shape needed to roll up per-project totals. */
export interface LineTotalsRow {
  rehab_project_id: string;
  estimated_cost: unknown;
  actual_cost: unknown;
  status?: string | null;
}

/**
 * Roll up rehab_line_items into per-project totals keyed by rehab_project_id.
 *
 * The budget total (`est`) spans every row; the spend total (`act`) sums the
 * actual cost of completed rows only, mirroring `recognizedSpend` in the
 * drawer so the card and the drawer report the same figure. Progress counts
 * (`done`, `total`)
 * include only budgeted rows (estimated_cost > 0), so a scope full of $0
 * placeholder rows never dilutes the completion % shown on the card / KPI
 * row (which read these counts via deriveProgressPct).
 */
export function aggregateLineTotals(
  rows: LineTotalsRow[] | null | undefined,
): Map<string, LineTotals> {
  const totals = new Map<string, LineTotals>();
  for (const r of rows ?? []) {
    const cur = totals.get(r.rehab_project_id) ?? { est: 0, act: 0, done: 0, total: 0 };
    const est = Number(r.estimated_cost) || 0;
    const act = Number(r.actual_cost) || 0;
    const isBudgeted = est > 0;
    const isCompleted = r.status === "completed";
    totals.set(r.rehab_project_id, {
      est: cur.est + est,
      act: cur.act + (isCompleted ? act : 0),
      done: cur.done + (isBudgeted && isCompleted ? 1 : 0),
      total: cur.total + (isBudgeted ? 1 : 0),
    });
  }
  return totals;
}

export function daysBetween(startIso: string | null, endIso: string | null): number {
  if (!startIso) return 0;
  const start = new Date(startIso).getTime();
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  return Math.max(0, Math.floor((end - start) / (1000 * 60 * 60 * 24)));
}

export const STATUS_LABEL: Record<RehabStatus, string> = {
  not_started: "Not started",
  in_progress: "In progress",
  completed: "Completed",
};
